
import React, { useRef, useState, useEffect } from 'react';
import { Product } from '../types';
import ProductCard from './ProductCard';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface ProductCarouselProps {
  title: string;
  products: Product[];
  isAdmin: boolean;
  onEdit: (product: Product) => void;
  onDelete: (id: string) => void;
  onTrack: (type: 'product' | 'news', id: string) => void;
  onClick?: (product: Product) => void;
}

const ProductCarousel: React.FC<ProductCarouselProps> = ({ title, products, isAdmin, onEdit, onDelete, onTrack, onClick }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [paddingStyle, setPaddingStyle] = useState({ paddingLeft: '1.25rem', paddingRight: '1.25rem' });

  useEffect(() => {
    const updatePadding = () => {
      const width = window.innerWidth;
      if (width < 1280) {
        setPaddingStyle({ paddingLeft: '1.25rem', paddingRight: '1.25rem' });
      } else {
        const calculated = Math.max(20, (width - 1280) / 2 + 24);
        setPaddingStyle({ 
          paddingLeft: `${calculated}px`, 
          paddingRight: `${calculated}px` 
        });
      }
    };
    updatePadding();
    window.addEventListener('resize', updatePadding);
    return () => window.removeEventListener('resize', updatePadding);
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const { current } = scrollRef;
      const scrollAmount = window.innerWidth < 768 ? 300 : 450;
      current.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    }
  };

  if (products.length === 0) return null;

  return (
    <div className="mb-16 relative group/carousel w-full overflow-hidden">
      {/* Título Alinhado ao Grid Central */}
      <div 
        className="w-full mb-8 font-sans"
        style={paddingStyle}
      >
         <div className="flex items-center gap-3 relative">
            <div className="absolute left-0 w-1.5 h-8 bg-primary rounded-full"></div>
            <h2 className="text-xl md:text-3xl font-black text-slate-900 uppercase tracking-tighter pl-4">
                {title}
            </h2>
         </div>
      </div>
      
      {/* Área de Scroll Full Width com Padding Dinâmico */}
      <div className="relative w-full">
        {/* Setas de Navegação nas Extremidades da Tela */}
        <button 
          onClick={() => scroll('left')}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-30 p-3 bg-white/80 backdrop-blur-md rounded-2xl shadow-xl text-slate-900 opacity-0 group-hover/carousel:opacity-100 transition-all hover:bg-primary hover:text-white"
        >
          <ChevronLeft size={24} />
        </button>
        <button 
          onClick={() => scroll('right')}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-30 p-3 bg-white/80 backdrop-blur-md rounded-2xl shadow-xl text-slate-900 opacity-0 group-hover/carousel:opacity-100 transition-all hover:bg-primary hover:text-white"
        >
          <ChevronRight size={24} />
        </button>

        <div 
          ref={scrollRef}
          className="flex gap-6 overflow-x-auto pb-10 scroll-smooth snap-x snap-mandatory hide-scrollbar"
          style={paddingStyle}
        >
          {products.map((product) => (
            <div key={product.id} className="min-w-[260px] md:min-w-[320px] max-w-[340px] snap-start">
              <ProductCard 
                product={product} 
                variant="vertical" 
                isAdmin={isAdmin} 
                onEdit={onEdit} 
                onDelete={onDelete} 
                onTrack={onTrack}
                onClick={onClick}
              />
            </div>
          ))}
          {/* Spacer para garantir o efeito de borda na rolagem final */}
          <div className="min-w-[40px] shrink-0"></div>
        </div>
      </div>

      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default ProductCarousel;
